import {
  CircleCheck,
  CircleMinus,
  CircleX,
  Info,
  Sparkles,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Tom = o que a situação significa, nunca a cor que se quer. `brand` é para o destaque
 * estratégico (§17: o amarelo é acento), `neutral` para o que está parado ou é só informação.
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand';

const TONES: Record<StatusTone, { icon: LucideIcon; variant: BadgeProps['variant'] }> = {
  success: { icon: CircleCheck, variant: 'success-soft' },
  warning: { icon: TriangleAlert, variant: 'warning-soft' },
  danger: { icon: CircleX, variant: 'destructive-soft' },
  info: { icon: Info, variant: 'info-soft' },
  neutral: { icon: CircleMinus, variant: 'neutral' },
  brand: { icon: Sparkles, variant: 'brand-soft' },
};

/**
 * Etiqueta de situação do sistema — ativo, pendente, com problema, inativo: o `Badge` do shadcn na
 * variante tingida do tom (contraste ≥ 4,5:1 nos dois temas, ADR-034), sempre com ícone: quem não
 * distingue a cor lê o ícone e o texto.
 *
 * O texto de cada situação vive no domínio (`api/*.ts`), não aqui: este componente só sabe
 * desenhar um tom.
 */
export function StatusBadge({
  tone,
  icon,
  children,
  className,
}: {
  tone: StatusTone;
  /** Ícone do domínio quando diz mais que o do tom (ex.: lápis para rascunho). */
  icon?: LucideIcon;
  children: ReactNode;
  className?: string;
}) {
  const Icon = icon ?? TONES[tone].icon;
  return (
    <Badge
      data-tone={tone}
      variant={TONES[tone].variant}
      className={cn('shrink-0 whitespace-nowrap px-1.5', className)}
    >
      <Icon aria-hidden />
      {children}
    </Badge>
  );
}
